import { useEffect, useState, useRef } from "react";
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { useFormik } from "formik";
import { FILTER_KEYS, FILTER_LIST } from "@Src/features/Filter/Constants/filters.constants";
import { useAppSelector } from "@App/hooks";
import { getBoardlist, getClassList, getSubjectList, getLanguageList } from "@Src/features/UserProfile/Services/UserProfile.sevice";
import { getChapterList, getTopicList } from "@Src/features/TestDetails/services/TestDetails.service";
import { APIResponse, APIStatus } from "@Src/services/baseInterfaces";

export const VideoListingFilter = ({ filters = {}, submitFilters, initialFilters = [] }: any) => {
    const coursePreferences = useAppSelector((state: any) => state.coursePreferences);
    const loginData = useAppSelector((state: any) => state.login);
    const languageId = useAppSelector((state: any) => state.login.LanguageId);
    const languageName = useAppSelector((state: any) => state.login.LanguageName);

    const isLoggedIn = loginData?.isLoggedIn;
    const filterContainerRef = useRef<HTMLDivElement>(null);

    const [localFilters, setLocalFilters] = useState<any>({
        [FILTER_LIST.BOARD]: [],
        [FILTER_LIST.CLASS]: [],
        [FILTER_LIST.LANGUAGE]: [],
        [FILTER_LIST.SUBJECT]: [],
        [FILTER_LIST.CHAPTER]: [],
        [FILTER_LIST.TOPIC]: []
    });

    const { handleChange, handleBlur, values, setValues, setFieldValue } = useFormik({
        initialValues: {
            [FILTER_KEYS.BOARD]: coursePreferences?.boardDetails?.[0] || null,
            [FILTER_KEYS.CLASS]: coursePreferences?.classDetails?.[0] || null,
            // [FILTER_KEYS.LANGUAGE]: (languageId && languageName) ? { Id: languageId, Name: languageName } : null,
            [FILTER_KEYS.LANGUAGE]: null,
            [FILTER_KEYS.SUBJECT]: null,
            [FILTER_KEYS.CHAPTER]: [],
            [FILTER_KEYS.TOPIC]: []
        },
        enableReinitialize: true,
        onSubmit: () => { },
    });

    // Initialize filters on mount
    useEffect(() => {
        fetchBoardList();
        fetchLanguageList();
    }, []);

    // Set initial filter values
    useEffect(() => {
        setInitialFilter();
    }, [initialFilters]);

    // Fetch class list when board changes
    useEffect(() => {
        if (values[FILTER_KEYS.BOARD]?.Id) {
            fetchClassList(values[FILTER_KEYS.BOARD].Id);
        }
    }, [values[FILTER_KEYS.BOARD]]);

    // Fetch subject list when class changes
    useEffect(() => {
        if (values[FILTER_KEYS.BOARD]?.Id && values[FILTER_KEYS.CLASS]?.Id) {
            fetchSubjectList(values[FILTER_KEYS.BOARD].Id, values[FILTER_KEYS.CLASS].Id);
        }
    }, [values[FILTER_KEYS.CLASS]]);

    // Fetch chapter list when subject changes
    useEffect(() => {
        if (values[FILTER_KEYS.BOARD]?.Id && values[FILTER_KEYS.CLASS]?.Id && values[FILTER_KEYS.SUBJECT]?.Id) {
            fetchChapterList(values[FILTER_KEYS.BOARD].Id, values[FILTER_KEYS.CLASS].Id, values[FILTER_KEYS.SUBJECT].Id);
        } else {
            setLocalFilters((prev: any) => ({ ...prev, [FILTER_LIST.CHAPTER]: [] }));
            setFieldValue(FILTER_KEYS.CHAPTER, []);
        }
    }, [values[FILTER_KEYS.SUBJECT]]);

    // Fetch topic list when chapters change
    useEffect(() => {
        if (values[FILTER_KEYS.BOARD]?.Id && values[FILTER_KEYS.CLASS]?.Id && values[FILTER_KEYS.SUBJECT]?.Id && values[FILTER_KEYS.CHAPTER]?.length > 0) {
            const chapterIds = values[FILTER_KEYS.CHAPTER].map((c: any) => c.Id);
            fetchTopicList(values[FILTER_KEYS.BOARD].Id, values[FILTER_KEYS.CLASS].Id, values[FILTER_KEYS.SUBJECT].Id,chapterIds);
        } else {
            setLocalFilters((prev: any) => ({ ...prev, [FILTER_LIST.TOPIC]: [] }));
            setFieldValue(FILTER_KEYS.TOPIC, []);
        }
    }, [values[FILTER_KEYS.CHAPTER]]);

    const fetchTopicList = async (boardId: number, classId: number, subjectId: number, chapterIds: number[]) => {
        try {
            const params = {
                boardId,
                classId,
                subjectId,
                chapterId: chapterIds.join(',')
            };
            const response: APIResponse = await getTopicList(params);
            if (response.ResponseType === APIStatus.SUCCESS) {
                const topics = response.data || [];
                setLocalFilters((prev: any) => ({ ...prev, [FILTER_LIST.TOPIC]: topics }));

                if (!topics.length) {
                    setFieldValue(FILTER_KEYS.TOPIC, []);
                } else if (values[FILTER_KEYS.TOPIC]?.length > 0) {
                    const availableTopicIds = topics.map((topic: any) => topic.Id);
                    const filteredTopics = values[FILTER_KEYS.TOPIC].filter((topic: any) => availableTopicIds.includes(topic.Id));
                    if (filteredTopics.length !== values[FILTER_KEYS.TOPIC].length) {
                        setFieldValue(FILTER_KEYS.TOPIC, filteredTopics);
                    }
                }
            } else {
                setLocalFilters((prev: any) => ({ ...prev, [FILTER_LIST.TOPIC]: [] }));
                setFieldValue(FILTER_KEYS.TOPIC, []);
            }
        } catch (error) {
            console.error('Error fetching topic list:', error);
            setLocalFilters((prev: any) => ({ ...prev, [FILTER_LIST.TOPIC]: [] }));
            setFieldValue(FILTER_KEYS.TOPIC, []);
        }
    };

    const setInitialFilter = () => {
        let newValues: any = { ...values };
        initialFilters.forEach((element: any) => {
            let keys = Object.keys(element);
            keys.forEach((key: any) => {
                if (key === FILTER_KEYS.SUBJECT && Array.isArray(element[key])) {
                    newValues[key] = element[key].length > 0 ? element[key][0] : null;
                } else {
                    newValues[key] = element[key];
                }
            });
        });
        setValues(newValues);
    };

    const fetchBoardList = async () => {
        try {
            const response: APIResponse = await getBoardlist();
            if (response.ResponseType === APIStatus.SUCCESS) {
                setLocalFilters((prev: any) => ({ ...prev, [FILTER_LIST.BOARD]: response.data || [] }));
            }
        } catch (error) {
            console.error('Error fetching examinations list:', error);
        }
    };

    const fetchLanguageList = async () => {
        try {
            const response: APIResponse = await getLanguageList();
            if (response.ResponseType === APIStatus.SUCCESS) {
                setLocalFilters((prev: any) => ({ ...prev, [FILTER_LIST.LANGUAGE]: response.data || [] }));
            }
        } catch (error) {
            console.error('Error fetching language list:', error);
        }
    };

    const fetchClassList = async (boardId: number) => {
        try {
            const response: APIResponse = await getClassList({ boardId });
            if (response.ResponseType === APIStatus.SUCCESS) {
                setLocalFilters((prev: any) => ({ ...prev, [FILTER_LIST.CLASS]: response.data || [] }));
            }
        } catch (error) {
            console.error('Error fetching class list:', error);
        }
    };

    const fetchSubjectList = async (boardId: number, classId: number) => {
        try {
            const response: APIResponse = await getSubjectList({ boardId, classId });
            if (response.ResponseType === APIStatus.SUCCESS) {
                setLocalFilters((prev: any) => ({ ...prev, [FILTER_LIST.SUBJECT]: response.data || [] }));
            }
        } catch (error) {
            console.error('Error fetching subject list:', error);
        }
    };

    const fetchChapterList = async (boardId: number, classId: number, subjectId: number) => {
        try {
            const params = {
                boardId,
                classId,
                subjectId: subjectId.toString()
            };
            const response: APIResponse = await getChapterList(params);
            if (response.ResponseType === APIStatus.SUCCESS) {
                setLocalFilters((prev: any) => ({ ...prev, [FILTER_LIST.CHAPTER]: response.data || [] }));
            }
        } catch (error) {
            console.error('Error fetching chapter list:', error);
        }
    };

    const handleOnChange = (field: string, event: any) => {
        let data: any = { ...values };
        data[field] = event.target.value;

        // Clear dependent fields when parent changes
        if (field === FILTER_KEYS.BOARD) {
            data[FILTER_KEYS.CLASS] = null;
            data[FILTER_KEYS.SUBJECT] = [];
            data[FILTER_KEYS.CHAPTER] = [];
            data[FILTER_KEYS.TOPIC] = [];
            setFieldValue(FILTER_KEYS.CLASS, null);
            setFieldValue(FILTER_KEYS.SUBJECT, []);
            setFieldValue(FILTER_KEYS.CHAPTER, []);
            setFieldValue(FILTER_KEYS.TOPIC, []);
        } else if (field === FILTER_KEYS.CLASS) {
            data[FILTER_KEYS.SUBJECT] = null;
            data[FILTER_KEYS.CHAPTER] = [];
            data[FILTER_KEYS.TOPIC] = [];
            setFieldValue(FILTER_KEYS.SUBJECT, null);
            setFieldValue(FILTER_KEYS.CHAPTER, []);
            setFieldValue(FILTER_KEYS.TOPIC, []);
        } else if (field === FILTER_KEYS.SUBJECT) {
            data[FILTER_KEYS.CHAPTER] = [];
            data[FILTER_KEYS.TOPIC] = [];
            setFieldValue(FILTER_KEYS.CHAPTER, []);
            setFieldValue(FILTER_KEYS.TOPIC, []);
        } else if (field === FILTER_KEYS.CHAPTER) {
            data[FILTER_KEYS.TOPIC] = [];
            setFieldValue(FILTER_KEYS.TOPIC, []);
        }

        submitFilters(data);
    };

    const onClickClearFilter = () => {
        const initialValues = {
            [FILTER_KEYS.BOARD]: coursePreferences?.boardDetails?.[0] || null,
            [FILTER_KEYS.CLASS]: coursePreferences?.classDetails?.[0] || null,
            // [FILTER_KEYS.LANGUAGE]: (languageId && languageName) ? { Id: languageId, Name: languageName } : null,
            [FILTER_KEYS.LANGUAGE]:  null,
            [FILTER_KEYS.SUBJECT]: null,
            [FILTER_KEYS.CHAPTER]: [],
            [FILTER_KEYS.TOPIC]: []
        };
        setValues(initialValues);
        submitFilters(initialValues);
    };

    const getMultiSelectLabel = (label: string, selectedItems: any[]) => {
        if (!selectedItems || selectedItems.length === 0) return label;

        const firstName = selectedItems[0]?.Name || '';
        const displayName = firstName.length > 4 ? firstName.substring(0, 4) + '...' : firstName;

        let result = `${label}: ${displayName}`;
        if (selectedItems.length > 1) {
            result += `, +${selectedItems.length - 1}`;
        }

        return result;
    };

    // Style the filter labels after render
    useEffect(() => {
        const styleFilterLabels = () => {
            if (filterContainerRef.current) {
                const labels = filterContainerRef.current.querySelectorAll('.p-multiselect-label:not(.p-placeholder)');
                labels.forEach((label: any) => {
                    const text = label.textContent;
                    if (text && (
                        text.includes('Subject:') ||
                        text.includes('Chapter:') ||
                        text.includes('Topic:')
                    )) {
                        const colonIndex = text.indexOf(':');
                        if (colonIndex > -1) {
                            const prefix = text.substring(0, colonIndex + 1);
                            const rest = text.substring(colonIndex + 1);
                            label.innerHTML = `<span class="filter-prefix">${prefix}</span>${rest}`;
                        }
                    }
                });
            }
        };

        const timer = setTimeout(styleFilterLabels, 100);
        return () => clearTimeout(timer);
    }, [values[FILTER_KEYS.SUBJECT], values[FILTER_KEYS.CHAPTER], values[FILTER_KEYS.TOPIC]]);

    console.log("localFilters", localFilters);
    return (
        <section className="filter-section ia-container px-3 py-6 m-auto">
            <div className="filter-head">
                <div className="f-h-heading">Choose a Filter to Refine Results</div>
            </div>
            <div className="filter-main-container">
                <div className="filter-dropdown-container" ref={filterContainerRef}>
                    {localFilters[FILTER_LIST.BOARD] && localFilters[FILTER_LIST.BOARD].length > 0 && (
                        <Dropdown
                            value={values[FILTER_KEYS.BOARD]}
                            name={FILTER_KEYS.BOARD}
                            onChange={(event) => { handleChange(event); handleOnChange(FILTER_KEYS.BOARD, event); }}
                            onBlur={handleBlur}
                            options={localFilters[FILTER_LIST.BOARD]}
                            optionLabel="Name"
                            dataKey="Id"
                            placeholder="Examination"
                            className="filter-dropdown"
                            appendTo="self"
                        />
                    )}



                    {localFilters[FILTER_LIST.CLASS] && localFilters[FILTER_LIST.CLASS].length > 0 && (
                        <Dropdown
                            value={values[FILTER_KEYS.CLASS]}
                            name={FILTER_KEYS.CLASS}
                            onChange={(event) => { handleChange(event); handleOnChange(FILTER_KEYS.CLASS, event); }}
                            onBlur={handleBlur}
                            options={localFilters[FILTER_LIST.CLASS]}
                            optionLabel="Name"
                            dataKey="Id"
                            placeholder="Class"
                            className="filter-dropdown"
                            appendTo="self"
                        />
                    )}

                    {localFilters[FILTER_LIST.LANGUAGE] && localFilters[FILTER_LIST.LANGUAGE].length > 0 && (
                        <Dropdown
                            value={values[FILTER_KEYS.LANGUAGE]}
                            name={FILTER_KEYS.LANGUAGE}
                            onChange={(event) => { handleChange(event); handleOnChange(FILTER_KEYS.LANGUAGE, event); }}
                            onBlur={handleBlur}
                            options={localFilters[FILTER_LIST.LANGUAGE]}
                            optionLabel="Name"
                            dataKey="Id"
                            placeholder="Language"
                            className="filter-dropdown"
                            appendTo="self"
                        />
                    )}

                    {localFilters[FILTER_LIST.SUBJECT] && localFilters[FILTER_LIST.SUBJECT].length > 0 && (
                        <Dropdown
                            value={values[FILTER_KEYS.SUBJECT]}
                            name={FILTER_KEYS.SUBJECT}
                            onChange={(event) => { handleChange(event); handleOnChange(FILTER_KEYS.SUBJECT, event); }}
                            onBlur={handleBlur}
                            options={localFilters[FILTER_LIST.SUBJECT]}
                            optionLabel="Name"
                            dataKey="Id"
                            placeholder="Subject"
                            className="filter-dropdown"
                            disabled={!values[FILTER_KEYS.CLASS]?.Id}
                            filter
                            appendTo="self"
                        />
                    )}


                    <MultiSelect
                        value={values[FILTER_KEYS.CHAPTER]}
                        name={FILTER_KEYS.CHAPTER}
                        onChange={(event) => { handleChange(event); handleOnChange(FILTER_KEYS.CHAPTER, event); }}
                        onBlur={handleBlur}
                        options={localFilters[FILTER_LIST.CHAPTER] && localFilters[FILTER_LIST.CHAPTER].length > 0 ? localFilters[FILTER_LIST.CHAPTER] : [{ Id: 'empty', Name: 'No chapters found' }]}
                        optionLabel="Name"
                        optionDisabled={(option) => option.Id === 'empty'}
                        placeholder="Chapter"
                        className="filter-dropdown"
                        disabled={!values[FILTER_KEYS.SUBJECT]?.Id}
                        maxSelectedLabels={0}
                        selectedItemsLabel={getMultiSelectLabel("Chapter", values[FILTER_KEYS.CHAPTER])}
                        filter
                        appendTo="self"
                        emptyFilterMessage="No chapters found"
                    />


                    <MultiSelect
                        value={values[FILTER_KEYS.TOPIC]}
                        name={FILTER_KEYS.TOPIC}
                        onChange={(event) => { handleChange(event); handleOnChange(FILTER_KEYS.TOPIC, event); }}
                        onBlur={handleBlur}
                        options={localFilters[FILTER_LIST.TOPIC] && localFilters[FILTER_LIST.TOPIC].length > 0 ? localFilters[FILTER_LIST.TOPIC] : [{ Id: 'empty', Name: 'No topics found' }]}
                        optionLabel="Name"
                        optionDisabled={(option) => option.Id === 'empty'}
                        placeholder="Topic"
                        className="filter-dropdown"
                        disabled={!values[FILTER_KEYS.CHAPTER] || values[FILTER_KEYS.CHAPTER].length === 0}
                        maxSelectedLabels={0}
                        selectedItemsLabel={getMultiSelectLabel("Topic", values[FILTER_KEYS.TOPIC])}
                        filter
                        appendTo="self"
                        emptyFilterMessage="No topics found"
                    />


                    <a className="ia-btn ia-btn-outline-primary tw-text-lg py-1 px-2" onClick={onClickClearFilter} style={{ cursor: 'pointer' }}>
                        <svg width="16" height="17" viewBox="0 0 16 17" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_3413_3245)">
                                <path d="M3.64216 3.45501C4.85187 2.40679 6.39949 1.83078 8.00016 1.83301C11.6822 1.83301 14.6668 4.81768 14.6668 8.49968C14.6668 9.92368 14.2202 11.2437 13.4602 12.3263L11.3335 8.49968H13.3335C13.3336 7.4541 13.0263 6.43155 12.4499 5.55919C11.8735 4.68682 11.0535 4.00312 10.0916 3.5931C9.12978 3.18307 8.06864 3.06482 7.04013 3.25303C6.01163 3.44124 5.06113 3.92761 4.30683 4.65168L3.64216 3.45501ZM12.3582 13.5443C11.1485 14.5926 9.60084 15.1686 8.00016 15.1663C4.31816 15.1663 1.3335 12.1817 1.3335 8.49968C1.3335 7.07568 1.78016 5.75568 2.54016 4.67301L4.66683 8.49968H2.66683C2.66674 9.54527 2.974 10.5678 3.55039 11.4402C4.12678 12.3125 4.94687 12.9962 5.90871 13.4063C6.87054 13.8163 7.93169 13.9345 8.96019 13.7463C9.9887 13.5581 10.9392 13.0717 11.6935 12.3477L12.3582 13.5443Z" />
                            </g>
                            <defs>
                                <clipPath id="clip0_3413_3245">
                                    <rect width="16" height="16" fill="white" transform="translate(0 0.5)" />
                                </clipPath>
                            </defs>
                        </svg>
                        Clear Filter
                    </a>
                </div>
            </div>

        </section>
    );
};
